import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import {
  createAdminSeason,
  deleteAdminSeason,
  getAdminSeasons,
  setDefaultAdminSeason,
  updateAdminSeason,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { unscopedKey } from "@/lib/queryClient";
import { ApiError } from "@/lib/api";
import type { Season } from "@/lib/types";
import { AdminIconButton } from "../components/AdminIconButton";
import { AdminMetricTile } from "../components/AdminMetricTile";
import { AdminPageHeader } from "../components/AdminPageHeader";

/**
 * `/admin/seasons` — global-admin season management.
 *
 * Seasons are globally shared objects: the system admin defines a
 * season once here and every team references the same list. They are
 * no longer a team sub-resource — a season created here is immediately
 * usable by all teams when they create matches.
 *
 * Delete is rejected by the backend (409) when a match still
 * references the season; the error surfaces inline in the confirm
 * dialog.
 */

function formatErr(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

function seasonDateRange(s: Season): string {
  if (s.starts_on && s.ends_on) return `${s.starts_on} → ${s.ends_on}`;
  if (s.starts_on) return `${s.starts_on} → ?`;
  if (s.ends_on) return `? → ${s.ends_on}`;
  return "no dates set";
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isCurrentSeason(season: Season, today: string): boolean {
  if (!season.starts_on && !season.ends_on) return false;
  if (season.starts_on && season.starts_on > today) return false;
  if (season.ends_on && season.ends_on < today) return false;
  return true;
}

export function AdminSeasonsPage() {
  const queryClient = useQueryClient();
  const seasonsQuery = useQuery({
    queryKey: unscopedKey("admin-seasons"),
    queryFn: getAdminSeasons,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Season | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Season | null>(null);

  const seasons = seasonsQuery.data ?? [];
  const defaultSeason = seasons.find((season) => season.is_default) ?? null;
  const seasonSignals = useMemo(() => {
    const today = todayIso();
    const current =
      seasons.find((season) => isCurrentSeason(season, today)) ?? null;
    const upcoming =
      [...seasons]
        .filter((season) => season.starts_on && season.starts_on > today)
        .sort((a, b) => a.starts_on.localeCompare(b.starts_on))[0] ?? null;
    const openEnded = seasons.filter(
      (season) => !season.starts_on || !season.ends_on,
    ).length;
    return { current, upcoming, openEnded };
  }, [seasons]);
  // Refresh both the admin list AND `/api/me` — the match season picker
  // (`MatchEditModal`) reads the global `me.seasons` array, which has a
  // 5-minute stale time, so a create/edit/delete here must invalidate
  // `me` or the picker shows the old season set until the next refetch.
  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-seasons"),
    });
    void queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
  };

  return (
    <div data-testid="admin-seasons" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Club setup"
        title="Season Calendar"
        description={
          <>
            Define the shared club seasons every squad uses for match context.
            The <span className="text-ink">default</span> season is inherited
            when a match has no explicit season.
          </>
        }
        meta={
          seasonsQuery.isFetching
            ? "Refreshing…"
            : `${seasons.length} season${seasons.length === 1 ? "" : "s"}`
        }
        actions={
          <AdminIconButton
            icon={Plus}
            label="New season"
            size="md"
            tone="accent"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-seasons-new"
          />
        }
      />

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <AdminMetricTile
          label="Default season"
          value={defaultSeason?.name ?? "None"}
          sub={defaultSeason ? seasonDateRange(defaultSeason) : "New matches need explicit seasons"}
          tone={defaultSeason ? "good" : "warn"}
          testid="admin-seasons-signal-default"
        />
        <AdminMetricTile
          label="Current context"
          value={seasonSignals.current?.name ?? "None"}
          sub={
            seasonSignals.current
              ? seasonDateRange(seasonSignals.current)
              : "Today is outside every dated season"
          }
          tone={seasonSignals.current ? "good" : "warn"}
          testid="admin-seasons-signal-current"
        />
        <AdminMetricTile
          label="Next season"
          value={seasonSignals.upcoming?.name ?? "None"}
          sub={
            seasonSignals.upcoming
              ? seasonDateRange(seasonSignals.upcoming)
              : "No upcoming dated season"
          }
          tone={seasonSignals.upcoming ? "busy" : "neutral"}
          testid="admin-seasons-signal-upcoming"
        />
        <AdminMetricTile
          label="Match fallback"
          value={defaultSeason ? "Configured" : "Missing"}
          sub={
            defaultSeason
              ? "Matches without a season inherit the default"
              : `${seasonSignals.openEnded} open-ended season${seasonSignals.openEnded === 1 ? "" : "s"}`
          }
          tone={defaultSeason ? "good" : "warn"}
          testid="admin-seasons-signal-total"
        />
      </div>

      {seasonsQuery.error ? (
        <p
          data-testid="admin-seasons-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(seasonsQuery.error)}
        </p>
      ) : null}

      {seasonsQuery.isLoading ? (
        <p className="text-xs font-mono text-ink-muted">Loading…</p>
      ) : seasons.length === 0 ? (
        <p
          data-testid="admin-seasons-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No seasons yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {seasons.map((s) => (
            <SeasonRow
              key={s.id}
              season={s}
              onEdit={() => setEditTarget(s)}
              onDelete={() => setDeleteTarget(s)}
              onChanged={invalidate}
            />
          ))}
        </ul>
      )}

      {createOpen ? (
        <SeasonComposer
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            void invalidate();
          }}
        />
      ) : null}
      {editTarget ? (
        <SeasonComposer
          mode="edit"
          season={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void invalidate();
          }}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteSeasonDialog
          season={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            void invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

/** One season row. Carries its own "Make default" mutation — pinning
 * the default is a per-row action, so the mutation can't live in the
 * parent's `.map`. */
function SeasonRow({
  season,
  onEdit,
  onDelete,
  onChanged,
}: {
  season: Season;
  onEdit: () => void;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const mut = useMutation({
    mutationFn: () => setDefaultAdminSeason(season.id),
    onSuccess: () => {
      toast.success(`${season.name} is now the default season`);
      onChanged();
    },
    onError: (err) => {
      toast.error("Couldn't set default season", {
        description: formatErr(err),
      });
    },
  });

  return (
    <li
      data-testid="admin-season-row"
      data-season-id={season.id}
      data-default={season.is_default ? "true" : undefined}
      className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-elevated px-2 py-1.5 text-xs"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-ink truncate">{season.name}</span>
          {season.is_default ? (
            <span
              data-testid="admin-season-default-badge"
              className="shrink-0 rounded border border-accent bg-accent px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-accent-ink"
            >
              Default
            </span>
          ) : null}
        </div>
        <div className="text-[0.6rem] font-mono text-ink-muted">
          {seasonDateRange(season)}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {season.is_default ? null : (
          <AdminIconButton
            icon={Check}
            label={mut.isPending ? "Setting default season" : "Make default season"}
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            data-testid="admin-season-set-default"
            tone="accent"
          />
        )}
        <AdminIconButton
          icon={Pencil}
          label="Edit season"
          onClick={onEdit}
          data-testid="admin-season-edit"
        />
        <AdminIconButton
          icon={Trash2}
          label="Delete season"
          onClick={onDelete}
          data-testid="admin-season-delete"
          tone="destructive"
        />
      </div>
    </li>
  );
}

function SeasonComposer({
  mode,
  season,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  season?: Season;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(season?.name ?? "");
  const [startsOn, setStartsOn] = useState(season?.starts_on ?? "");
  const [endsOn, setEndsOn] = useState(season?.ends_on ?? "");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      mode === "create"
        ? createAdminSeason({ name, starts_on: startsOn, ends_on: endsOn })
        : updateAdminSeason(season!.id, {
            name,
            starts_on: startsOn,
            ends_on: endsOn,
          }),
    onSuccess: () => {
      toast.success(mode === "create" ? "Season created" : "Season updated");
      onSaved();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error(
        mode === "create" ? "Couldn't create season" : "Couldn't update season",
        { description: formatErr(err) },
      );
    },
  });

  return (
    <ConfirmDialog
      title={mode === "create" ? "New season" : "Edit season"}
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="admin-seasons-composer-name"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Starts on
              </span>
              <TkticDateInput
                value={startsOn}
                onChange={setStartsOn}
                data-testid="admin-seasons-composer-starts"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Ends on
              </span>
              <TkticDateInput
                value={endsOn}
                onChange={setEndsOn}
                data-testid="admin-seasons-composer-ends"
              />
            </label>
          </div>
          {error ? (
            <p
              data-testid="admin-seasons-composer-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={
        mut.isPending
          ? "Saving…"
          : mode === "create"
            ? "Create season"
            : "Save changes"
      }
      onConfirm={() => {
        setError(null);
        if (!name.trim()) {
          setError("Name is required.");
          return;
        }
        if (startsOn && endsOn && startsOn > endsOn) {
          setError("Start date must be on or before end date.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-seasons-composer"
    />
  );
}

function DeleteSeasonDialog({
  season,
  onClose,
  onDeleted,
}: {
  season: Season;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => deleteAdminSeason(season.id),
    onSuccess: () => {
      toast.success("Season deleted");
      onDeleted();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't delete season", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Delete season"
      danger
      message={
        <div className="flex flex-col gap-2 text-xs">
          <p className="text-ink">
            Delete <span className="font-display">{season.name}</span>? This
            removes it for every team.
          </p>
          {error ? (
            <p
              data-testid="admin-seasons-delete-error"
              className="text-destructive font-mono"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Deleting…" : "Delete season"}
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-seasons-delete"
    />
  );
}
